import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { CrewBoard } from '../components/porters/CrewBoard';
import { WeeklyPlanner } from '../components/porters/WeeklyPlanner';
import { WhoWithWho } from '../components/porters/WhoWithWho';
import { PortersTab } from '../components/porters/PortersTab';
import { ymd, errMsg } from '../components/porters/porterUi';

const TABS = [['board', 'Live Crew Board'], ['who', "Who's Working With Who"], ['planner', 'Weekly Planner'], ['porters', 'Porters'], ['history', 'History']];

const HistoryTab = ({ authFetch }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { authFetch('get', '/admin/crew/history?limit=200').then(({ data }) => setItems(data.history)).catch((e) => toast.error(errMsg(e))); }, [authFetch]);
  if (!items) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="crew-history">
      {items.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">No allocation changes yet.</div>}
      {items.map(h => (
        <div key={h.id} className="p-3 text-sm flex flex-wrap gap-x-3 gap-y-1">
          <span className="text-xs text-slate-500 w-32 shrink-0">{new Date(h.at + 'Z').toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          <strong className="text-[color:var(--mv-navy)]">{h.porter_name}</strong>
          <span className="uppercase text-[10px] font-black tracking-wider bg-slate-100 rounded px-1.5 py-0.5 self-center">{h.action}</span>
          <span className="text-slate-600">{h.previous ? <>{h.previous} → </> : null}{h.new}</span>
          <span className="text-xs text-slate-400 ml-auto">by {h.by}</span>
        </div>
      ))}
    </div>
  );
};

function PortersInner() {
  const { authFetch } = useAuth();
  const [tab, setTab] = useState('board');
  const [date, setDate] = useState(() => ymd(new Date()));
  return (
    <div className="min-h-[60vh] bg-slate-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/admin" className="h-9 w-9 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" title="Back to dashboard" data-testid="porters-back"><ArrowLeft className="w-4 h-4 text-slate-600" /></Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Crew management</div>
            <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Porters</h1>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto mb-5 border-b border-slate-200" data-testid="porters-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`shrink-0 h-11 px-4 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === k ? 'border-[color:var(--mv-orange)] text-[color:var(--mv-navy)]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`porters-tab-${k}`}>{l}</button>
          ))}
        </div>
        {tab === 'board' && <CrewBoard authFetch={authFetch} date={date} setDate={setDate} />}
        {tab === 'who' && <WhoWithWho authFetch={authFetch} date={date} setDate={setDate} />}
        {tab === 'planner' && <WeeklyPlanner authFetch={authFetch} onOpenDay={(d) => { setDate(d); setTab('board'); }} />}
        {tab === 'porters' && <PortersTab authFetch={authFetch} />}
        {tab === 'history' && <HistoryTab authFetch={authFetch} />}
      </div>
    </div>
  );
}

export default function AdminPorters() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!user) return <LoginForm title="Move Van Ltd · Owner" subtitle="Sign in to manage porters." expectedRole="owner" />;
  if (user.role !== 'owner') return <div className="min-h-[60vh] flex items-center justify-center text-slate-600">Owners only.</div>;
  return <PortersInner />;
}
